import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select';
import { IssueStatus } from '@momentum/types';
import { useGoalStatus } from '@/features/goals/hierarchy-tree/goal-card/hooks/useGoalStatus';

interface InlineStatusSelectorProps {
  value: string | IssueStatus;
  onValueChange: (value: IssueStatus) => void;
  disabled?: boolean;
}

export const InlineStatusSelector: React.FC<InlineStatusSelectorProps> = ({
  value,
  onValueChange,
  disabled = false,
}) => {
  const { t } = useTranslation('goals');
  const statusConfig = useGoalStatus(value as IssueStatus);
  const StatusIcon = statusConfig.icon;
  const nonNullValue = value || t('statusUnknown');

  return (
    <Select
      value={nonNullValue}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <SelectTrigger
        className={`h-7 min-h-0 w-auto min-w-[120px] px-2 py-1 text-xs ${statusConfig.className} border`}
      >
        <div className="flex items-center gap-1.5">
          <StatusIcon className={`h-3 w-3 ${statusConfig.iconColor}`} />
          <SelectValue>{statusConfig.label}</SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent>
        {!Object.values(IssueStatus).includes(value as IssueStatus) && (
          <SelectItem value={nonNullValue}>{nonNullValue}</SelectItem>
        )}
        <SelectItem value={IssueStatus.TODO}>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-gray-300" />
            {t('statusTodo')}
          </div>
        </SelectItem>
        <SelectItem value={IssueStatus.IN_PROGRESS}>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-blue-500" />
            {t('statusInProgress')}
          </div>
        </SelectItem>
        <SelectItem value={IssueStatus.COMPLETED}>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            {t('statusCompleted')}
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
};
